'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["href", "relAttr"],
  _excluded2 = ["fileMeta"];
import { Component } from 'react';
import getComponentName from 'react-utils/getComponentName';
import getIsFileManagerUrl from 'FileManagerLib/utils/isFileManagerUrl';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import { getEmailSystemHublVar, getEmailSubscriptionLinkDisplayName, getHublVariable, hasHublVariable, getLinkCopyDataByHublVar } from '../../utils/hubl';
import { makeCancelable } from '../../utils/makeCancelable';
import { getUrlParts, getMeetingLinkSlug, isMeetingLink, getRelAttrTrackingValues } from '../../utils/link';
import { pick, deepCompare } from '../../utils/iterables';
import { LinkTypes, TrackableLinkFields, ExternalLinkTypes, i18nKey } from '../../constants/link';
import { INSERT_LINK, UPDATE_LINK } from '../../constants/usageTracking';
import { deserializeAnchorFromLink } from '../../utils/anchor';
import { PopoverBodyMode } from '../../constants/popover';
import { jsx as _jsx } from "react/jsx-runtime";
const withLinkState = WrappedComponent => {
  class ManagedLinks extends Component {
    constructor(props) {
      super(props);
      this.handleUpdate = update => {
        // Update Popover State
        this.setState(Object.assign({}, update), () => {
          if (!this.initialLinkInfo) {
            this.initialLinkInfo = this.state;
          }
          const hasValidValue = this.state.href || this.state.fileMeta;

          // Store this fully updated link state
          if (hasValidValue) {
            this.lastCompleteLinkState = this.state;
          }
        });
      };
      this.handleSave = (...args) => {
        this.props.onSave(this.state, ...args);
        this.lastSavedLinkState = Object.assign({}, this.state, {
          isValidLink: true
        });
        this.trackLinkUsage();
      };
      this.handleLinkTypeChange = linkType => {
        const href = linkType === LinkTypes.VIEW_AS_WEBPAGE ? '{{ view_as_page_url }}' : '';
        this.setState({
          linkType,
          href,
          fileMeta: null,
          externalLink: ExternalLinkTypes.includes(linkType),
          isValidLink: true
        });
      };
      this.handleUnlink = () => {
        this.setState({
          linkType: LinkTypes.NONE,
          href: '',
          fileMeta: null,
          isValidLink: true
        }, this.handleSave);
      };
      this.handleClose = () => {
        const {
          onClose
        } = this.props;
        this.setState(Object.assign({}, this.lastSavedLinkState || this.initialLinkInfo));
        if (onClose) onClose();
      };
      const {
        linkText,
        href: _href,
        relAttr,
        externalLink
      } = props;
      this.state = {
        linkText,
        href: _href,
        relAttr,
        externalLink,
        linkType: LinkTypes.URL,
        linkDisplayName: _href,
        isValidLink: true
      };
    }
    componentDidMount() {
      this.fetchLinkType();
    }
    componentDidUpdate({
      mode: prevMode,
      target: prevTarget
    }) {
      const {
        allowRawLinksOnly,
        mode,
        linkText,
        target
      } = this.props;
      if (prevTarget !== target) {
        const {
          href,
          relAttr,
          externalLink
        } = this.props;
        this.initialLinkInfo = null;
        this.lastSavedLinkState = null;
        this.setState({
          linkText,
          href,
          relAttr,
          externalLink,
          linkType: href ? LinkTypes.URL : LinkTypes.NONE,
          linkDisplayName: href,
          isValidLink: true
        });
        this.fetchLinkType();
      } else if (prevMode !== mode && mode === PopoverBodyMode.PREVIEW) {
        // Restore link state to last completed
        this.setState(Object.assign({}, this.lastSavedLinkState || this.initialLinkInfo, {
          linkText
        }));
      } else if (allowRawLinksOnly && (linkText !== this.state.linkText || linkText !== this.state.href) && mode === PopoverBodyMode.PREVIEW) {
        this.setState({
          href: linkText,
          linkDisplayName: linkText,
          linkText
        });
      }
    }
    componentWillUnmount() {
      this.linkTypePromise.cancel();
    }
    trackLinkUsage() {
      if (!this.props.trackLinkUsage) return;

      // We want to track changes to href but not the href value itself
      const pickTrackingChangeValues = pick([...TrackableLinkFields, 'href']);
      const initialTrackingValues = pickTrackingChangeValues(this.initialLinkInfo || {});
      const _trackingValues = pickTrackingChangeValues(this.state);
      if (!deepCompare(_trackingValues, initialTrackingValues)) {
        const trackingAction = initialTrackingValues.linkType === LinkTypes.NONE ? INSERT_LINK : UPDATE_LINK;
        const {
            relAttr
          } = _trackingValues,
          trackingValues = _objectWithoutPropertiesLoose(_trackingValues, _excluded);
        this.props.trackLinkUsage(trackingAction, Object.assign({}, trackingValues, getRelAttrTrackingValues(relAttr)));
      }
    }
    checkForFileLink(href) {
      const {
        fetchFileFromUrl,
        fetchFileByUrl
      } = this.props;
      const getLinkInfo = fileData => ({
        linkType: LinkTypes.FILE,
        linkDisplayName: `${fileData.name}.${fileData.extension}`,
        fileMeta: {
          fileName: `${fileData.name}.${fileData.extension}`,
          size: fileData.size,
          type: fileData.type
        }
      });
      if (fetchFileByUrl) {
        return new Promise((resolve, reject) => {
          fetchFileByUrl(href).then(fileData => resolve(getLinkInfo(fileData))).catch(reject);
        });
      }
      return new Promise((resolve, reject) => {
        fetchFileFromUrl(encodeURIComponent(href)).then(fileData => resolve(getLinkInfo(fileData))).catch(reject);
      });
    }
    checkForPageUrl(href) {
      const {
        fetchPageBySlug
      } = this.props;
      const {
        domain,
        slug
      } = getUrlParts(href);
      return new Promise((resolve, reject) => {
        fetchPageBySlug(slug, domain).then(({
          objects: pages
        }) => {
          if (pages && pages.length) {
            // The array of pages will now contain redirects, so if we don't
            // match a page by url we default to the first redirect page
            const matchingPage = pages.find(({
              path
            }) => path === `/${slug}`) || pages[0];
            resolve({
              linkType: LinkTypes.PAGE,
              linkDisplayName: matchingPage.name
            });
          } else {
            resolve(this.getExternalUrl(href));
          }
        }).catch(reject);
      });
    }
    checkForMeetingLink(href) {
      const {
        fetchMeetingBySlug
      } = this.props;
      const slug = getMeetingLinkSlug(href);
      return new Promise((resolve, reject) => {
        fetchMeetingBySlug(slug).then(meeting => {
          if (meeting && meeting.link === href) {
            resolve({
              linkType: LinkTypes.MEETING,
              linkDisplayName: meeting.name || meeting.link
            });
          } else {
            resolve(this.getExternalUrl(href));
          }
        }).catch(reject);
      });
    }
    checkForAnchorLink(href) {
      const anchorName = deserializeAnchorFromLink(href);
      return new Promise((resolve, reject) => {
        this.props.fetchAnchors(anchorName, true) // Pass in `true` for an exact match
        .then(anchors => {
          if (anchors && anchors.length) {
            resolve({
              linkType: LinkTypes.ANCHOR,
              linkDisplayName: anchorName
            });
          } else {
            resolve(this.getExternalUrl(href));
          }
        }).catch(reject);
      });
    }
    getExternalUrl(href) {
      return {
        linkType: LinkTypes.URL,
        linkDisplayName: href
      };
    }
    fetchLinkType() {
      const {
        href,
        useNoLinkOption,
        insertLink,
        allowMeetingLink,
        allowAnchorLinks,
        allowFileDownload,
        allowEmailWebVersionLink
      } = this.props;
      this.linkTypePromise = makeCancelable(new Promise((resolve, reject) => {
        if (!href) {
          if (useNoLinkOption && !insertLink) {
            resolve({
              linkType: LinkTypes.NONE,
              linkDisplayName: ''
            });
          } else {
            resolve(this.getExternalUrl(href));
          }
        } else if (href.startsWith('mailto:')) {
          resolve({
            linkType: LinkTypes.EMAIL,
            linkDisplayName: href.split('mailto:')[1]
          });
        } else if (href.toLocaleLowerCase().startsWith('tel:')) {
          resolve(this.getExternalUrl(href));
        } else if (allowAnchorLinks && href.startsWith('#')) {
          this.checkForAnchorLink(href).then(resolve).catch(reject);
        } else if (getHublVariable(href)) {
          const linkCopyDataByHublVar = getLinkCopyDataByHublVar(href);
          if (linkCopyDataByHublVar && allowEmailWebVersionLink) {
            resolve({
              linkType: getHublVariable(href),
              linkDisplayName: I18n.text(i18nKey(linkCopyDataByHublVar.text))
            });
          } else if (getEmailSystemHublVar(href)) {
            resolve({
              linkType: LinkTypes.EMAIL_SUBSCRIPTION,
              linkDisplayName: getEmailSubscriptionLinkDisplayName(href)
            });
          } else {
            resolve(this.getExternalUrl(href));
          }
        } else if (hasHublVariable(href)) {
          resolve(this.getExternalUrl(href));
        } else if (allowFileDownload && getIsFileManagerUrl(href)) {
          this.checkForFileLink(href).then(resolve).catch(() => {
            this.checkForPageUrl(href).then(resolve).catch(reject);
          });
        } else if (allowMeetingLink && isMeetingLink(href)) {
          this.checkForMeetingLink(href).then(resolve).catch(reject);
        } else {
          this.checkForPageUrl(href).then(resolve).catch(reject);
        }
      }));
      this.linkTypePromise.promise.then(data => {
        this.handleUpdate(data, false);
      }).catch(error => {
        if (error.isCanceled) {
          console.warn('linkType fetch cancelled in `withLinkState`');
        } else {
          // Throw a new error so sentries can log API failures and we
          // can trace it back to `withLinkState`
          throw new Error(error.message);
        }
      });
    }
    render() {
      const _this$state = this.state,
        {
          fileMeta
        } = _this$state,
        linkData = _objectWithoutPropertiesLoose(_this$state, _excluded2);
      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, {
        onLinkTypeChange: this.handleLinkTypeChange,
        onUpdate: this.handleUpdate,
        onSave: this.handleSave,
        onUnlink: this.handleUnlink,
        dropdownClassName: this.props.dropdownClassName,
        showRefAndTargetControls: this.props.showRefAndTargetControls,
        fetchPages: this.props.fetchPages,
        onSelectFile: this.props.onSelectFile,
        onClose: this.handleClose,
        currentFileMeta: fileMeta
      }, linkData));
    }
  }
  ManagedLinks.propTypes = {
    fetchPageBySlug: PropTypes.func.isRequired,
    fetchMeetingBySlug: PropTypes.func.isRequired,
    trackLinkUsage: PropTypes.func,
    onUpdate: PropTypes.func,
    onSave: PropTypes.func,
    onClose: PropTypes.func,
    onLinkTypeChange: PropTypes.func,
    linkText: PropTypes.string,
    href: PropTypes.string,
    relAttr: PropTypes.array,
    externalLink: PropTypes.bool,
    showRefAndTargetControls: PropTypes.bool,
    fetchFileFromUrl: PropTypes.func,
    fetchFileByUrl: PropTypes.func,
    fetchPages: PropTypes.func,
    fetchAnchors: PropTypes.func,
    onSelectFile: PropTypes.func,
    dropdownClassName: PropTypes.string,
    useNoLinkOption: PropTypes.bool,
    insertLink: PropTypes.bool,
    mode: PropTypes.string,
    allowMeetingLink: PropTypes.bool,
    allowAnchorLinks: PropTypes.bool,
    allowFileDownload: PropTypes.bool,
    allowRawLinksOnly: PropTypes.bool,
    allowEmailWebVersionLink: PropTypes.bool,
    target: PropTypes.any
  };
  ManagedLinks.displayName = `withLinkState(${getComponentName(WrappedComponent)})`;
  return ManagedLinks;
};
export default withLinkState;